import { z } from "zod";

export const LAUT_GROUPS = ["a", "ä", "au", "b", "ch", "d", "e", "ei", "eu", "f", "g", "h", "i", "j", "k", "l", "m", "n", "ng", "o", "ö", "p", "pf", "r", "s", "sch", "t", "u", "ü", "w", "z"] as const;
export const lautGroupSchema = z.enum(LAUT_GROUPS);
export type LautGroup = z.infer<typeof lautGroupSchema>;
export const lautGroupExamples: Record<LautGroup, string> = {
  a: "Affe", ä: "Ähre", au: "Auto", b: "Baum", ch: "ich / Bach", d: "Dach", e: "Esel", ei: "Eis / Mai", eu: "Eule / Häuser", f: "Fisch / Vogel", g: "Gabel", h: "Haus", i: "Igel", j: "Jacke", k: "Kuh", l: "Lampe", m: "Maus", n: "Nase", ng: "Ring", o: "Ofen", ö: "Öl", p: "Pilz", pf: "Pferd", r: "Rose", s: "Sonne / Haus", sch: "Schaf", t: "Tasche", u: "Uhr", ü: "Tür", w: "Wiese", z: "Zahn",
};
export function lautGroupLabel(group: LautGroup | "other" | null | undefined) {
  return group === "other" ? "Anderer / nicht eindeutig zuordenbarer Laut" : group ? `${group} wie ${lautGroupExamples[group]}` : "Nicht erfasst";
}

// Filled only after the explicit listening review. Paths are bundled, never user URLs.
export const LAUTE_AUDIO_ASSETS: Readonly<Record<string, { word: string; src: string }>> = {};
export const LAUTE_AUDIO_RELEASED = false;
export const lauteWordSchema = z.object({
  word: z.string().trim().min(1).max(120).regex(/^[A-Za-zÄÖÜäöüß]+$/u, "Bitte ein einzelnes deutsches Wort eingeben.")
    .refine((word) => !/([bcdfghjklmnpqrstvwxzß])\1|ck|tz/iu.test(word), "Doppelkonsonanten sowie ck und tz sind ausgeschlossen."),
  soundCount: z.number().int().min(1).max(40),
  initialSound: lautGroupSchema,
  finalSound: lautGroupSchema,
  confirmed: z.boolean().refine((value) => value, "Lautangaben bitte fachlich bestätigen."),
  audioId: z.string().max(120).optional(),
}).strict().superRefine((entry, context) => {
  if (entry.audioId && LAUTE_AUDIO_ASSETS[entry.audioId]?.word !== entry.word) context.addIssue({ code: "custom", path: ["audioId"], message: "Für dieses Wort ist keine geprüfte Aufnahme freigegeben." });
});
export type LauteWord = z.infer<typeof lauteWordSchema>;
export const lauteWordsSchema = z.array(lauteWordSchema).min(1).max(5_000).superRefine((words, context) => {
  const keys = words.map(({ word }) => word.normalize("NFC").toLocaleLowerCase("de-DE"));
  if (new Set(keys).size !== keys.length) context.addIssue({ code: "custom", message: "Jedes Wort darf nur einmal vorkommen." });
});
export const lauteRunSettingsSchema = z.object({
  position: z.enum(["initial", "final"]).default("initial"),
  soundCounts: z.array(z.number().int().min(1).max(40)).max(40).default([]),
  count: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]).default(10),
});
export const lauteExerciseConfigSchema = z.object({
  instructions: z.string().trim().min(1).max(2_000).default("Höre gut zu und nenne den ersten oder letzten Laut."),
  words: lauteWordsSchema,
  defaultPosition: z.enum(["initial", "final"]).default("initial"),
  defaultCount: z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20)]).default(10),
});
export const lauteAnswerSchema = z.object({
  type: z.literal("laute-erkennen"),
  itemId: z.string().min(1).max(240),
  rating: z.enum(["correct", "uncertain", "incorrect"]),
  responseState: z.enum(["answered", "omitted"]),
  spokenSound: z.union([lautGroupSchema, z.literal("other")]).nullable().default(null),
}).superRefine((answer, context) => {
  if (answer.responseState === "omitted" && (answer.spokenSound !== null || answer.rating !== "incorrect")) context.addIssue({ code: "custom", message: "Keine Antwort erhält keine Lautangabe und zählt als ausgelassen." });
});
export type LauteAnswer = z.infer<typeof lauteAnswerSchema>;
export type LauteExerciseConfig = z.infer<typeof lauteExerciseConfigSchema>;
export type LauteRunSettings = z.infer<typeof lauteRunSettingsSchema>;

// Editorial draft: counts describe spoken units, never spelling length. Awaiting audio/content review.
export const LAUTE_DRAFT_WORDS: LauteWord[] = [
  ["Haus", 3, "h", "s"], ["Maus", 3, "m", "s"], ["Baum", 3, "b", "m"],
  ["Buch", 3, "b", "ch"], ["Dach", 3, "d", "ch"], ["Schaf", 3, "sch", "f"],
  ["Fisch", 3, "f", "sch"], ["Tasche", 4, "t", "e"], ["Schule", 4, "sch", "e"],
  ["Eule", 3, "eu", "e"], ["Auge", 3, "au", "e"], ["Nase", 4, "n", "e"],
  ["Rose", 4, "r", "e"], ["Hase", 4, "h", "e"], ["Hose", 4, "h", "e"],
  ["Schuh", 2, "sch", "u"], ["Kuh", 2, "k", "u"], ["See", 2, "s", "e"],
  ["Hand", 4, "h", "t"], ["Hund", 4, "h", "t"], ["Zahn", 3, "z", "n"],
  ["Reis", 3, "r", "s"], ["Seil", 3, "s", "l"], ["Rad", 3, "r", "t"],
].map(([word, soundCount, initialSound, finalSound]) => ({ word, soundCount, initialSound, finalSound, confirmed: true }) as LauteWord);
